export const COMPOSER_FOOTER_COMPACT_BREAKPOINT_PX = 620;
export const COMPOSER_FOOTER_WIDE_ACTIONS_COMPACT_BREAKPOINT_PX = 780;
export const COMPOSER_PRIMARY_ACTIONS_COMPACT_BREAKPOINT_PX =
  COMPOSER_FOOTER_WIDE_ACTIONS_COMPACT_BREAKPOINT_PX;

/**
 * Release-only slack, in the same spirit as RESTING_CONTROLS_SLACK_PX below.
 *
 * Going compact is unconditional, or the trailing controls clip. Leaving it
 * has to earn a pixel first: the form is measured with clientWidth, which
 * rounds a fractional layout box, so a Composer parked on the threshold can
 * report both sides of it on consecutive resize entries and swap the footer
 * back and forth for as long as the pointer sits still.
 */
const COMPOSER_FOOTER_COMPACT_SLACK_PX = 1;

function isBelowCompactBreakpoint(
  width: number | null,
  breakpoint: number,
  previous: boolean | undefined,
): boolean {
  return width !== null && width < breakpoint + (previous ? COMPOSER_FOOTER_COMPACT_SLACK_PX : 0);
}
/**
 * Module-private on purpose: the count is only meaningful through
 * getRestingComposerImagePreviewCounts, which is what every caller wants.
 */
const RESTING_COMPOSER_IMAGE_THUMBNAIL_LIMIT = 3;

/**
 * Whether the footer folds its trailing controls into the overflow menu.
 *
 * The Composer form's own width decides this, never the viewport and never
 * whether the Composer is Expanded or Resting. `previous` is the footer state
 * being replaced, which is what buys the slack above.
 */
export function shouldUseCompactComposerFooter(
  width: number | null,
  options?: { hasWideActions?: boolean; previous?: boolean },
): boolean {
  const breakpoint = options?.hasWideActions
    ? COMPOSER_FOOTER_WIDE_ACTIONS_COMPACT_BREAKPOINT_PX
    : COMPOSER_FOOTER_COMPACT_BREAKPOINT_PX;
  return isBelowCompactBreakpoint(width, breakpoint, options?.previous);
}

/**
 * Whether the send cluster drops its labels, on the same width and the same
 * slack. Only a footer already carrying wide actions can reach it.
 */
export function shouldUseCompactComposerPrimaryActions(
  width: number | null,
  options?: { hasWideActions?: boolean; previous?: boolean },
): boolean {
  if (!options?.hasWideActions) {
    return false;
  }
  return isBelowCompactBreakpoint(
    width,
    COMPOSER_PRIMARY_ACTIONS_COMPACT_BREAKPOINT_PX,
    options.previous,
  );
}

export function getRestingComposerImagePreviewCounts(imageCount: number): {
  visibleCount: number;
  overflowCount: number;
} {
  const visibleCount = Math.min(imageCount, RESTING_COMPOSER_IMAGE_THUMBNAIL_LIMIT);
  return {
    visibleCount,
    overflowCount: Math.max(0, imageCount - visibleCount),
  };
}

export function shouldUseRestingComposerLayout(input: {
  isExistingThread: boolean;
  isMobileViewport: boolean;
  isFocused: boolean;
  isScrollCollapsed: boolean;
  hasExpandedChrome: boolean;
  collapseOnBlur: boolean;
  hasMultilinePrompt?: boolean | undefined;
}): boolean {
  // A scroll collapse rests the Composer regardless of the blur preference:
  // the person asked for it with the gesture, and it lifts on the next
  // Composer interaction. With blur collapse off, losing focus alone never
  // rests the Composer.
  const collapsed = input.isScrollCollapsed || (input.collapseOnBlur && !input.isFocused);
  return (
    input.isExistingThread &&
    !input.isMobileViewport &&
    collapsed &&
    !input.hasExpandedChrome &&
    !input.hasMultilinePrompt
  );
}

/**
 * How much taller the empty Expanded Composer is than its resting row on
 * desktop widths, from the layout classes in ChatComposer: the body loses
 * 8px of top padding, the prompt clamps from min-h-17.5 (70px) to 32px, and
 * the 48px footer leaves flow.
 */
export const COMPOSER_RESTING_EXPANSION_MIN_PX = 94;

/**
 * The space the timeline reserves at its end for the Composer overlay.
 *
 * The overlay is measured live, but a Resting Composer is much shorter than
 * an Expanded Composer. Reserving only the resting height lets a scroll to the
 * end land flush against the short Composer, and the expansion that follows
 * then covers the last rows because the timeline never moves for footer
 * growth. While resting, the reservation keeps the last expanded height, or at
 * least the resting height plus the empty expansion, so expanding again
 * changes nothing above the Composer. An expanded measurement is authoritative
 * and may shrink it.
 */
export function resolveComposerTimelineInset(input: {
  currentInset: number;
  overlayHeight: number;
  isResting: boolean;
}): number {
  return input.isResting
    ? Math.max(input.currentInset, input.overlayHeight + COMPOSER_RESTING_EXPANSION_MIN_PX)
    : input.overlayHeight;
}

export function shouldAnimateComposerRestingTransition(input: {
  hasCompletedInitialLayout: boolean;
  stateChanged: boolean;
  hasInterruptedAnimation: boolean;
}): boolean {
  return input.hasCompletedInitialLayout && (input.stateChanged || input.hasInterruptedAnimation);
}

export interface RestingComposerControlsMeasurement {
  gap: number;
  naturalFixedWidth: number;
  minimumFixedWidth: number;
  blockWidths: readonly number[];
  overflowWidth: number;
}

function restingComposerControlsWidth(
  input: RestingComposerControlsMeasurement,
  hiddenCount: number,
  fixedWidth = input.naturalFixedWidth,
): number {
  const { blockWidths, gap } = input;
  const visibleCount = blockWidths.length - hiddenCount;
  return (
    fixedWidth +
    blockWidths.slice(0, visibleCount).reduce((sum, width) => sum + width, 0) +
    (hiddenCount > 0 ? input.overflowWidth : 0) +
    gap * (visibleCount + (hiddenCount > 0 ? 1 : 0))
  );
}

/**
 * The width the resting controls take with nothing moved into overflow.
 *
 * The context strip reserves this much for the Composer before deciding
 * whether its own labels may expand. Judging against the currently visible
 * controls instead lets the strip expand into space the Composer just gave
 * up, which shrinks the host, hides the controls again, and repeats.
 */
export function resolveRestingComposerControlsNaturalWidth(
  input: RestingComposerControlsMeasurement,
): number {
  return restingComposerControlsWidth(input, 0);
}

const RESTING_CONTROLS_SLACK_PX = 1;

/**
 * Decide how many trailing resting control blocks move into the overflow
 * menu, and whether the cluster can show at all, from natural widths.
 *
 * Trailing blocks hide before the model picker shrinks. Once they are all in
 * the overflow menu, the picker may contract to its minimum readable width;
 * below that the whole cluster hides rather than clipping.
 */
export function resolveRestingComposerControlsLayout(
  input: RestingComposerControlsMeasurement & {
    hostWidth: number;
    previous?: { hiddenCount: number; visible: boolean };
  },
): { hiddenCount: number; visible: boolean } {
  const { blockWidths, hostWidth, previous } = input;
  let hiddenCount = 0;
  while (
    hiddenCount < blockWidths.length &&
    restingComposerControlsWidth(input, hiddenCount) > hostWidth
  ) {
    hiddenCount += 1;
  }
  // Growing the overflow menu is unconditional, or the controls would clip.
  // Shrinking it has to earn a pixel of slack first: the picker is flexible,
  // so its natural width is recovered from a truncated label whose
  // scrollWidth is integral while the rendered box is fractional. The
  // Composer re-measures on every render, so without that margin a host
  // sitting exactly on a threshold flips a block in and out until React
  // gives up with "Maximum update depth exceeded".
  if (previous) {
    const previousHiddenCount = Math.min(previous.hiddenCount, blockWidths.length);
    while (
      hiddenCount < previousHiddenCount &&
      restingComposerControlsWidth(input, hiddenCount) > hostWidth - RESTING_CONTROLS_SLACK_PX
    ) {
      hiddenCount += 1;
    }
  }
  const minimumWidth = restingComposerControlsWidth(input, hiddenCount, input.minimumFixedWidth);
  const visible =
    previous && !previous.visible
      ? minimumWidth <= hostWidth - RESTING_CONTROLS_SLACK_PX
      : minimumWidth <= hostWidth;
  return { hiddenCount, visible };
}

export function shouldUseMobileComposerLayout(input: {
  readonly isMobileViewport: boolean;
  readonly isFocused: boolean;
  readonly hasExpandedChrome: boolean;
  readonly hasMultilinePrompt?: boolean | undefined;
}): boolean {
  return (
    input.isMobileViewport &&
    !input.isFocused &&
    !input.hasExpandedChrome &&
    !input.hasMultilinePrompt
  );
}
